import type {
  MarketplaceOrderDeliverable,
  MarketplaceOrderStatus,
  MarketplaceSubmissionUnit,
} from '@foodfluence/shared';
import { deliverableLabel } from '@foodfluence/shared';

/** True when every deliverable slot has been submitted by the creator (status advanced past pendingSubmission). */
export function marketplaceOrderUploadContentSubmitted(
  deliverables: Record<string, MarketplaceOrderDeliverable> | undefined,
  submissions: Record<string, MarketplaceSubmissionUnit> | undefined,
): boolean {
  if (!deliverables || Object.keys(deliverables).length === 0) return false;
  const subs = submissions ?? {};
  for (const slotKey of Object.keys(deliverables)) {
    const unit = subs[slotKey];
    if (!unit || !unit.status || unit.status === 'pendingSubmission') return false;
  }
  return true;
}

export type BrandApprovalStatus = 'not_submitted' | 'in_review' | 'revision_requested' | 'approved';

/** Derives the overall brand-approval state from submission data. */
export function marketplaceOrderBrandApprovalStatus(
  deliverables: Record<string, MarketplaceOrderDeliverable> | undefined,
  submissions: Record<string, MarketplaceSubmissionUnit> | undefined,
  orderStatus: MarketplaceOrderStatus,
): BrandApprovalStatus {
  if (orderStatus === 'completed') return 'approved';
  if (!deliverables || Object.keys(deliverables).length === 0) return 'not_submitted';
  const subs = submissions ?? {};
  const units = Object.keys(deliverables).map((k) => subs[k]).filter(Boolean);
  if (units.length === 0) return 'not_submitted';
  if (units.every((u) => u.status === 'approved')) return 'approved';
  if (units.some((u) => u.status === 'revisionRequested')) return 'revision_requested';
  if (units.some((u) => u.status && u.status !== 'pendingSubmission')) return 'in_review';
  return 'not_submitted';
}

export function marketplaceOrderAcceptanceTimeLeftLabel(createdAtSeconds?: number | null): string | null {
  if (!createdAtSeconds || !Number.isFinite(createdAtSeconds)) return null;
  const createdAt = new Date(createdAtSeconds * 1000);
  const deadline = new Date(createdAt.getTime() + 72 * 60 * 60 * 1000);
  const msLeft = deadline.getTime() - Date.now();
  if (msLeft <= 0) return '0h left';
  const hours = Math.max(0, Math.ceil(msLeft / (60 * 60 * 1000)));
  const days = Math.floor(hours / 24);
  if (days >= 1) return `${days}d left`;
  return `${hours}h left`;
}

export function marketplaceOrderInviteExpiryTimeLeftLabel(inviteExpiresAtSeconds?: number | null): string | null {
  if (!inviteExpiresAtSeconds || !Number.isFinite(inviteExpiresAtSeconds)) return null;
  const deadline = new Date(inviteExpiresAtSeconds * 1000);
  const msLeft = deadline.getTime() - Date.now();
  if (msLeft <= 0) return '0h left';
  const hours = Math.max(0, Math.ceil(msLeft / (60 * 60 * 1000)));
  const days = Math.floor(hours / 24);
  if (days >= 1) return `${days}d left`;
  return `${hours}h left`;
}

export function formatMarketplaceOrderDateShort(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
}

export function formatMarketplaceOrderDateLong(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
}

export function formatDueDateLabel(iso?: string | null): string | null {
  const short = formatMarketplaceOrderDateShort(iso);
  return short ? `Due ${short}` : null;
}

export function marketplaceOrderStatusPill(status: MarketplaceOrderStatus): { label: string; bg: string; color: string } {
  if (status === 'pendingCreatorAcceptance')
    return { label: 'Pending acceptance', bg: 'rgba(255,255,255,0.9)', color: '#f54900' };
  if (status === 'inviteExpired')
    return { label: 'Invite expired', bg: 'rgba(255,255,255,0.9)', color: '#b45309' };
  if (status === 'creatorCountered')
    return { label: 'Counter pending', bg: 'rgba(255,255,255,0.9)', color: '#1d4ed8' };
  if (status === 'pendingCompletion') return { label: 'In progress', bg: 'rgba(255,255,255,0.9)', color: '#1c1917' };
  if (status === 'creatorRejected') return { label: 'Declined', bg: 'rgba(255,255,255,0.9)', color: '#44403b' };
  if (status === 'adminCanceled') return { label: 'Canceled', bg: 'rgba(255,255,255,0.9)', color: '#ef4444' };
  return { label: 'Completed', bg: 'rgba(255,255,255,0.9)', color: '#1c1917' };
}

export function listMarketplaceDeliverables(deliverables?: Record<string, MarketplaceOrderDeliverable> | null) {
  if (!deliverables) return [];
  const reqs = Object.values(deliverables);
  const map = new Map<string, number>();
  for (const r of reqs) {
    const label = deliverableLabel(r.type);
    map.set(label, (map.get(label) ?? 0) + 1);
  }
  return [...map.entries()].map(([label, count]) => ({ label, count }));
}

export function marketplaceOrderBundleTitle(
  deliverables: Array<{ label: string; count: number }>
): string {
  if (!deliverables.length) return 'Marketplace Package';

  const parts = deliverables.map(({ label, count }) => {
    const base = label.replace(/^Instagram\s+/i, '');
    if (count <= 1) return base;

    const plural =
      base === 'Story'
        ? 'Stories'
        : base.endsWith('s')
          ? base
          : `${base}s`;
    return `${count} ${plural}`;
  });

  return `${parts.join(' + ')} Bundle`;
}

export function marketplaceOrderTotalRequiredCount(reqs: Record<string, MarketplaceOrderDeliverable> | undefined): number {
  if (!reqs) return 0;
  return Object.keys(reqs).length;
}

export function marketplaceOrderDeliverableDisplayName(r: MarketplaceOrderDeliverable): string {
  return deliverableLabel(r.type);
}

export function marketplaceOrderIsCanceled(status: MarketplaceOrderStatus): boolean {
  return status === 'adminCanceled';
}

export function marketplaceOrderStepperState(
  status: MarketplaceOrderStatus,
  deliverables?: Record<string, MarketplaceOrderDeliverable>,
  submissions?: Record<string, MarketplaceSubmissionUnit>,
): { active: 0 | 1 | 2 | 3 } {
  if (status === 'completed') return { active: 3 };
  const approval = marketplaceOrderBrandApprovalStatus(deliverables, submissions, status);
  if (approval === 'approved') return { active: 3 };
  if (approval === 'in_review' || approval === 'revision_requested') return { active: 2 };
  if (marketplaceOrderUploadContentSubmitted(deliverables, submissions)) return { active: 2 };
  if (status === 'pendingCompletion') return { active: 1 };
  return { active: 0 };
}

/** Prefer brief-level links (checkout flow); fall back to links on deliverable rows. */
export function marketplaceOrderInspirationLinks(
  orderBrief: unknown,
  deliverables?: Record<string, MarketplaceOrderDeliverable> | null
): string[] {
  const raw = (orderBrief as { requirements?: { inspirationLinks?: unknown } } | null)?.requirements?.inspirationLinks;
  const fromBrief = Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string' && Boolean(x.trim())) : [];
  if (fromBrief.length > 0) return [...new Set(fromBrief)];
  const reqs = deliverables ? Object.values(deliverables) : [];
  const fromReqs = reqs.flatMap((r) => r.inspirationLinks ?? []).filter(Boolean) as string[];
  return [...new Set(fromReqs)];
}

/**
 * Brand-uploaded inspiration assets (images/videos) from the brief. The backend serves these
 * as signed read URLs; raw storage paths won't load, so the caller must use a signed source.
 */
export function marketplaceOrderInspirationAssetUrls(orderBrief: unknown): string[] {
  const raw = (orderBrief as { requirements?: { inspirationAssetUrls?: unknown } } | null)?.requirements
    ?.inspirationAssetUrls;
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.filter((x): x is string => typeof x === 'string' && Boolean(x.trim())))];
}
